import { formatPhoneNumberForDisplay } from '@/lib/countryDialCodes'
import type { RegistrationField } from '../types'

export const formatPhoneNumber = (phoneNumber: string | null | undefined): string => {
  if (!phoneNumber) return '-'

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '')

  // Format based on length
  if (cleaned.length === 10) {
    // Format: (123) 456-7890 or 012-345-6789
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  } else if (cleaned.length === 11) {
    // Format with country code: +1 (234) 567-8901
    return `+${cleaned.slice(0, 1)} ${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  } else if (cleaned.length > 11) {
    // International format: +XX XXX-XXX-XXXX
    const countryCode = cleaned.slice(0, cleaned.length - 10)
    const rest = cleaned.slice(-10)
    return `+${countryCode} ${rest.slice(0, 3)}-${rest.slice(3, 6)}-${rest.slice(6)}`
  }

  // Return as-is if doesn't match common formats
  return phoneNumber
}

export const formatFieldValue = (field: RegistrationField, value: any): string => {
  if (!value || value === '') return '-'

  // Phone Number fields always store '+<dial code><number>', so they are shown
  // by their own country code rather than guessed at from the digit count.
  if (field.kind === 'phone_number') {
    return formatPhoneNumberForDisplay(value)
  }

  // Check for phone field by identifier
  if (field.identifier === 'phone') {
    return formatPhoneNumber(value)
  }

  switch (field.kind) {
    case 'multiple_choice':
      return Array.isArray(value) ? value.join(', ') : value
    case 'single_choice':
    case 'dropdown':
      const option = field.options.find(opt => opt.value === value)
      return option ? option.label : value
    case 'session_choice': {
      // Answers store the agenda_session_id; show the session title instead.
      const session = field.session_options?.find(opt => String(opt.agenda_session_id) === String(value))
      return session?.agenda_session?.name || value
    }
    case 'consent_text':
      return value === true || value === 'true' ? 'Yes' : 'No'
    case 'long_text':
      return value
    default:
      return value
  }
}

export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }) + ', ' + date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })
}
